import { Quote } from "lucide-react";

/**
 * Testimonials — auto-scrolling marquee.
 * Each card shows a stylised brand badge (org monogram with brand-flavored colour)
 * plus an excerpt from the reference letter and author details.
 */

export const TESTIMONIALS = [
  {
    id: "wits",
    org: "Wits Business School",
    monogram: "WBS",
    color: "#003b71",
    accent: "#fdb924",
    quote:
      "Rekisa provided students with practical exposure to GTM strategy, bringing strong industry knowledge and practical insights that added significant value to the learning experience.",
    author: "Emmanuel Silva Quaye, PhD",
    role: "Senior Lecturer & Programme Director, PGDM-BA",
  },
  {
    id: "gibs",
    org: "GIBS",
    sub: "Gordon Institute of Business Science",
    monogram: "GIBS",
    color: "#00205b",
    accent: "#c8102e",
    quote:
      "His ability to translate complex digital growth concepts into practical, accessible learning has made him a valuable partner to the GIBS EDA.",
    author: "Guinivere Pedro",
    role: "Head, Entrepreneurship Development Academy",
  },
  {
    id: "sloane",
    org: "22 On Sloane",
    monogram: "22",
    color: "#1e1e1e",
    accent: "#f5a623",
    quote:
      "His ability to translate digital growth concepts into practical, accessible learning has made Rekisa a valuable partner in supporting entrepreneurs within our ecosystem.",
    author: "Nomfundo Mdluli",
    role: "Programme Manager, 22 On Sloane",
  },
  {
    id: "cloud",
    org: "Cloud On Demand",
    monogram: "COD",
    color: "#0a66c2",
    accent: "#34a853",
    quote:
      "Rekisa is a capable partner for organisations seeking support with marketing enablement, digital demand generation, cloud-focused campaigns, and B2B performance marketing.",
    author: "Rantobeng Matjie",
    role: "Team Leader, Cloud On Demand",
  },
  {
    id: "sabo",
    org: "SA Books Online",
    sub: "SABO",
    monogram: "SABO",
    color: "#c0392b",
    accent: "#f1c40f",
    quote:
      "I can confidently recommend Rekisa to any organisation seeking a knowledgeable, dependable, and results-oriented e-commerce and strategy partner.",
    author: "Sbusiso Manqa",
    role: "Founder & Managing Director, SA Books Online",
  },
  {
    id: "mashesha",
    org: "Mashesha Consulting",
    monogram: "MC",
    color: "#2a9d8f",
    accent: "#e9c46a",
    quote:
      "Rekisa provided exceptional support in enhancing our sales and marketing outreach. Their expertise in building campaigns through Apollo allowed us to efficiently reach potential customers at scale.",
    author: "Thulani Gama",
    role: "Managing Director, Mashesha Consulting",
  },
  {
    id: "mah",
    org: "MAH CloudQuest",
    monogram: "MAH",
    color: "#4f46e5",
    accent: "#06b6d4",
    quote:
      "The results have been truly transformative for our business. They didn't just run campaigns for us — they taught us how to build our own lead generation machine.",
    author: "Thato Mohono",
    role: "Technology Lead, MAH CloudQuest",
  },
  {
    id: "innovative",
    org: "Innovative Engineering Hub",
    monogram: "IEH",
    color: "#1f2937",
    accent: "#f97316",
    quote:
      "Their structured, data-driven approach to sales and marketing has helped us move towards a more professional and scalable business development process.",
    author: "Thabo Molibeli",
    role: "Innovative Engineering Hub",
  },
];

function OrgLogo({ t }) {
  return (
    <div className="flex items-center gap-3">
      <div
        className="relative h-12 w-12 rounded-xl flex items-center justify-center font-display font-black text-white shrink-0"
        style={{ background: t.color }}
      >
        <span className="text-[13px] leading-none tracking-tight">{t.monogram}</span>
        <span
          aria-hidden
          className="absolute -bottom-1 -right-1 h-3.5 w-3.5 rounded-md"
          style={{ background: t.accent }}
        />
      </div>
      <div className="leading-tight">
        <div className="font-display text-sm font-bold text-zinc-900">{t.org}</div>
        {t.sub && <div className="text-[11px] text-zinc-500">{t.sub}</div>}
      </div>
    </div>
  );
}

function TestimonialCard({ t }) {
  return (
    <article
      data-testid={`testimonial-${t.id}`}
      className="relative shrink-0 w-[88vw] sm:w-[460px] rounded-3xl border border-zinc-200 bg-white p-6 sm:p-7 shadow-[0_10px_30px_-15px_rgba(0,0,0,0.1)]"
    >
      <Quote
        aria-hidden
        className="absolute top-5 right-5 h-7 w-7 text-zinc-200"
      />
      <OrgLogo t={t} />
      <p className="mt-5 text-[15px] sm:text-base text-zinc-800 leading-relaxed">
        “{t.quote}”
      </p>
      <div className="mt-5 pt-4 border-t border-zinc-100 flex items-center gap-3">
        <div
          className="h-9 w-9 rounded-full flex items-center justify-center text-white text-xs font-bold"
          style={{ background: t.color }}
        >
          {t.author
            .split(" ")
            .map((n) => n[0])
            .slice(0, 2)
            .join("")}
        </div>
        <div className="leading-tight">
          <div className="text-sm font-semibold text-zinc-900">{t.author}</div>
          <div className="text-xs text-zinc-500">{t.role}</div>
        </div>
      </div>
    </article>
  );
}

export default function TestimonialsSection() {
  // Duplicate the list so the marquee can loop seamlessly.
  const items = [...TESTIMONIALS, ...TESTIMONIALS];

  return (
    <section
      data-testid="testimonials-section"
      className="relative py-24 sm:py-32 bg-white overflow-hidden"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8 mb-12">
        <div className="max-w-3xl">
          <div className="text-[11px] tracking-[0.2em] uppercase text-zinc-500 font-mono mb-4">
            Social proof
          </div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-[-0.03em] leading-[1.05] text-zinc-900">
            Trusted by leading institutions.
          </h2>
          <p className="mt-5 text-zinc-600 max-w-2xl text-lg">
            From business schools to enterprise tech partners — here's what they've said about working with Rekisa.
          </p>
        </div>
      </div>

      {/* Fade edges */}
      <div className="relative">
        <div
          aria-hidden
          className="pointer-events-none absolute left-0 top-0 bottom-0 w-24 sm:w-40 z-10"
          style={{ background: "linear-gradient(to right, #ffffff, rgba(255,255,255,0))" }}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute right-0 top-0 bottom-0 w-24 sm:w-40 z-10"
          style={{ background: "linear-gradient(to left, #ffffff, rgba(255,255,255,0))" }}
        />

        <div className="flex w-max gap-5 animate-marquee-x will-change-transform">
          {items.map((t, i) => (
            <TestimonialCard key={`${t.id}-${i}`} t={t} />
          ))}
        </div>
      </div>
    </section>
  );
}
